import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, Brain, Download, Dumbbell, FileText, HeartPulse,
  IdCard, KeyRound, Loader2, Pill, Plus, Save, ShieldAlert, ShieldCheck, Unlink, Upload, Watch,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, API } from '../lib/api';
import { openFile, openPdf } from '../lib/downloads';
import { isUserLoggedIn, getUser } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart, RadialBar, RadialBarChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

const emptyMedication = { name: '', dosage: '', schedule: '', refill_date: '', notes: '' };

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-11 w-11 rounded-2xl bg-[#1E3F33] text-white grid place-items-center shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h2 className="font-display text-2xl text-[#1A1A1A] leading-tight">{title}</h2>
        <p className="text-sm text-[#7A7A7A] mt-1">{subtitle}</p>
      </div>
    </div>
  );
}

function ResultList({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="mt-4 space-y-2 text-sm text-[#1A1A1A]">
      {items.map((item, index) => (
        <li key={index} className="rounded-xl bg-[#F4F1EA] px-4 py-3">{item}</li>
      ))}
    </ul>
  );
}

const toNumber = (value) => {
  if (value === null || value === undefined || value === '-') return null;
  const n = Number(String(value).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : null;
};

const riskLevelScore = (level) => ({ low: 28, moderate: 58, high: 84, emergency: 100 }[String(level || '').toLowerCase()] || 0);

const splitHistoryItems = (value = '') => String(value)
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const normalizeHistoryItem = (item = '') => {
  const parts = String(item).split(':').map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return { group: parts[0], label: parts.slice(1).join(': ') };
  }
  return { group: '', label: item.trim() };
};

const parseMedicalHistory = (history = '') => {
  const text = String(history || '').trim();
  if (!text || text === 'Not reported') {
    return { plain: 'Not reported', symptoms: [], positives: [], negatives: [], other: [] };
  }

  const result = { plain: '', symptoms: [], positives: [], negatives: [], other: [] };
  text.split(';').map((section) => section.trim()).filter(Boolean).forEach((section) => {
    const [rawTitle, ...rest] = section.split(':');
    const title = rawTitle.trim().toLowerCase();
    const value = rest.join(':').trim();
    if (!value) {
      result.other.push(section);
      return;
    }
    if (title.includes('general symptom screen')) {
      result.symptoms.push(...splitHistoryItems(value).map((label) => ({ group: '', label })));
    } else if (title.includes('full checkup positives')) {
      result.positives.push(...splitHistoryItems(value).map(normalizeHistoryItem));
    } else if (title.includes('full checkup negatives')) {
      result.negatives.push(...splitHistoryItems(value).map(normalizeHistoryItem));
    } else {
      result.other.push(section);
    }
  });

  if (!result.symptoms.length && !result.positives.length && !result.negatives.length && !result.other.length) {
    result.plain = text;
  }
  return result;
};

function HistoryPills({ items, tone = 'neutral', limit = 14 }) {
  if (!items?.length) return null;
  const visible = items.slice(0, limit);
  const hidden = items.slice(limit);
  const toneClass = {
    positive: 'border-[#B8D8C2] bg-[#F1FAF4] text-[#1E3F33]',
    muted: 'border-[#D8DDE5] bg-white text-[#4A4A4A]',
    neutral: 'border-[#E8E1D5] bg-[#F4F1EA] text-[#1A1A1A]',
  }[tone] || 'border-[#E8E1D5] bg-[#F4F1EA] text-[#1A1A1A]';
  const renderItem = (item, index) => (
    <span key={`${item.group}-${item.label}-${index}`} className={`inline-flex max-w-full items-center rounded-full border px-3 py-1 text-xs font-medium ${toneClass}`}>
      {item.group ? <span className="mr-1 text-[#7A7A7A]">{item.group}:</span> : null}
      <span className="truncate">{item.label}</span>
    </span>
  );

  return (
    <div className="flex flex-wrap gap-2">
      {visible.map(renderItem)}
      {hidden.length ? (
        <details className="w-full">
          <summary className="cursor-pointer text-xs font-semibold text-[#4F7CAC]">Show {hidden.length} more checked items</summary>
          <div className="mt-2 flex flex-wrap gap-2">
            {hidden.map(renderItem)}
          </div>
        </details>
      ) : null}
    </div>
  );
}

function EmptyChart({ label = 'No chart data yet' }) {
  return (
    <div className="h-[180px] rounded-xl border border-dashed border-[#D8DDE5] bg-white grid place-items-center text-sm text-[#7A7A7A]">
      {label}
    </div>
  );
}

function ProfileField({ label, value }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 min-w-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-[#7A7A7A]">{label}</div>
      <div className="mt-1 text-sm font-semibold text-[#1A1A1A] break-words">{value || '-'}</div>
    </div>
  );
}

export default function HealthHub() {
  const nav = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [hub, setHub] = useState(null);
  const [medications, setMedications] = useState([emptyMedication]);
  const [risk, setRisk] = useState({ age: '', weight_kg: '', height_cm: '', systolic_bp: '', diastolic_bp: '', blood_sugar_mg_dl: '', family_history: '' });
  const [wearable, setWearable] = useState({ heart_rate: '', sleep_hours: '', steps: '', spo2: '' });
  const [emergencyText, setEmergencyText] = useState('');
  const [emergencyResult, setEmergencyResult] = useState(null);
  const [contact, setContact] = useState({ name: '', phone: '' });
  const [record, setRecord] = useState({ record_type: 'prescription', title: '', details: '', record_date: '' });
  const [uploadRecord, setUploadRecord] = useState({ record_type: 'uploaded_report', title: '', record_date: '', notes: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [diet, setDiet] = useState({ condition: '', goal: '', calories: '', activity_level: '' });
  const [mental, setMental] = useState({ mood: 3, stress: 3, sleep_quality: 3, notes: '' });
  const [busy, setBusy] = useState('');
  const [wearableToken, setWearableToken] = useState('');
  const [abhaOpen, setAbhaOpen] = useState(false);
  const [abhaStep, setAbhaStep] = useState('id');
  const [abhaId, setAbhaId] = useState('');
  const [abhaOtp, setAbhaOtp] = useState('');
  const [showEmergencyOnboarding, setShowEmergencyOnboarding] = useState(() => {
    return localStorage.getItem('hb_emergency_onboarding_seen') !== 'true';
  });

  const refresh = async () => {
    const { data } = await api.get('/health-hub');
    setHub(data);
    if (data.medications?.length) setMedications(data.medications);
  };

  const fetchWearableToken = async () => {
    try {
      const { data } = await api.get('/health-hub/wearables/token');
      setWearableToken(data.token);
    } catch (_) {
      toast.error('Could not load wearable sync token');
    }
  };

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (['profile', 'abha', 'meds', 'risk', 'wearable', 'emergency', 'records', 'coach', 'mental'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    if (!isUserLoggedIn()) return nav('/login');
    (async () => {
      try {
        await refresh();
        await fetchWearableToken();
      } catch (_) {
        toast.error('Could not load Health Hub');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
      await refresh();
      toast.success('Saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally {
      setBusy('');
    }
  };

  const uploadHealthRecord = async () => {
    if (!uploadFile) {
      toast.error('Please choose a PDF or image report');
      return;
    }
    await run('upload-record', async () => {
      const form = new FormData();
      form.append('file', uploadFile);
      form.append('record_type', uploadRecord.record_type);
      form.append('title', uploadRecord.title);
      form.append('record_date', uploadRecord.record_date);
      form.append('notes', uploadRecord.notes);
      await api.post('/health-hub/records/upload', form);
      setUploadFile(null);
      setUploadRecord({ record_type: 'uploaded_report', title: '', record_date: '', notes: '' });
    });
  };

  const requestAbhaOtp = async (e) => {
    e.preventDefault();
    setBusy('abha-request');
    try {
      await api.post('/abha/link/request', { abha_id: abhaId.trim() });
      setAbhaStep('otp');
      toast.success('ABDM OTP sent to the mobile linked with this ABHA');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start ABHA link');
    } finally {
      setBusy('');
    }
  };

  const verifyAbhaOtp = async (e) => {
    e.preventDefault();
    setBusy('abha-verify');
    try {
      const { data } = await api.post('/abha/link/verify', { abha_id: abhaId.trim(), otp: abhaOtp.trim() });
      localStorage.setItem('hb_user', JSON.stringify(data.user));
      setAbhaOpen(false);
      setAbhaStep('id');
      setAbhaId('');
      setAbhaOtp('');
      await refresh();
      toast.success('ABHA linked');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid ABHA OTP');
    } finally {
      setBusy('');
    }
  };

  const unlinkAbha = async () => {
    setBusy('abha-unlink');
    try {
      const { data } = await api.post('/abha/unlink');
      localStorage.setItem('hb_user', JSON.stringify(data.user));
      await refresh();
      toast.success('ABHA removed from Health Buddy');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not unlink ABHA');
    } finally {
      setBusy('');
    }
  };

  const riskPayload = useMemo(() => ({
    age: Number(risk.age),
    weight_kg: Number(risk.weight_kg),
    height_cm: Number(risk.height_cm),
    systolic_bp: risk.systolic_bp ? Number(risk.systolic_bp) : null,
    diastolic_bp: risk.diastolic_bp ? Number(risk.diastolic_bp) : null,
    blood_sugar_mg_dl: risk.blood_sugar_mg_dl ? Number(risk.blood_sugar_mg_dl) : null,
    family_history: risk.family_history.split(',').map((x) => x.trim()).filter(Boolean),
  }), [risk]);

  const compiledProfile = useMemo(() => {
    if (!hub) return null;
    const latestProfile = hub.profiles?.[0] || {};
    const meds = hub.medications || [];
    const riskData = hub.risk?.input || {};
    const riskResults = hub.risk || {};
    const wearableData = hub.wearable?.metrics || {};
    const dietData = hub.diet_fitness || {};
    const mentalData = hub.mental_health || {};
    
    return {
      medical_history: latestProfile.medical_history || riskData.medical_history || 'Not reported',
      age: riskData.age || latestProfile.age || '-',
      weight: riskData.weight_kg || latestProfile.weight || '-',
      height: riskData.height_cm || latestProfile.height || '-',
      bmi: riskResults.bmi || '-',
      bp: (riskData.systolic_bp && riskData.diastolic_bp) ? `${riskData.systolic_bp}/${riskData.diastolic_bp} mmHg` : '-',
      sugar: riskData.blood_sugar_mg_dl ? `${riskData.blood_sugar_mg_dl} mg/dL` : '-',
      medications: meds.length ? meds : (latestProfile.medications || []),
      family_history: riskData.family_history?.length ? riskData.family_history : (latestProfile.family_history || []),
      diet: dietData.goal || latestProfile.diet || 'Not reported',
      exercise: dietData.exercise_plan?.join(', ') || latestProfile.exercise || 'Not reported',
      sleep: wearableData.sleep_hours || '-',
      steps: wearableData.steps || '-',
      hr: wearableData.heart_rate || '-',
      spo2: wearableData.spo2 || '-',
      mood: mentalData.input?.mood || '-',
      stress: mentalData.input?.stress || '-',
      emergency_contacts: hub.emergency_contacts?.length ? hub.emergency_contacts : (latestProfile.emergency_contacts || []),
      predictions: latestProfile.predictions || null,
      suggestions: latestProfile.suggestions || riskResults.suggestions || dietData.suggestions || ''
    };
  }, [hub]);

  const medicalHistory = useMemo(
    () => parseMedicalHistory(compiledProfile?.medical_history),
    [compiledProfile?.medical_history]
  );

  const chartData = useMemo(() => {
    if (!compiledProfile) return null;
    const systolic = toNumber(String(compiledProfile.bp).split('/')[0]);
    const diastolic = toNumber(String(compiledProfile.bp).split('/')[1]);
    const sugar = toNumber(compiledProfile.sugar);
    const bmi = toNumber(compiledProfile.bmi);
    const sleep = toNumber(compiledProfile.sleep);
    const steps = toNumber(compiledProfile.steps);
    const hr = toNumber(compiledProfile.hr);
    const spo2 = toNumber(compiledProfile.spo2);
    const vitals = [
      { name: 'BMI', value: bmi, target: 24, color: '#2A5244' },
      { name: 'Systolic', value: systolic, target: 120, color: '#E07A5F' },
      { name: 'Diastolic', value: diastolic, target: 80, color: '#4F7CAC' },
      { name: 'Sugar', value: sugar, target: 100, color: '#C89B3C' },
    ].filter((x) => x.value !== null);
    const wearableSeries = [
      { name: 'Sleep', value: sleep, target: 7 },
      { name: 'Steps', value: steps ? Math.min(100, Math.round((steps / 10000) * 100)) : null, target: 80 },
      { name: 'Heart', value: hr, target: 72 },
      { name: 'SpO2', value: spo2, target: 98 },
    ].filter((x) => x.value !== null);
    const riskScores = [
      { name: 'Heart', score: hub?.risk?.heart_risk ?? riskLevelScore(compiledProfile.predictions?.bp_risk), fill: '#D9534F' },
      { name: 'Diabetes', score: hub?.risk?.diabetes_risk ?? riskLevelScore(compiledProfile.predictions?.sugar_risk), fill: '#C89B3C' },
      { name: 'BP', score: hub?.risk?.hypertension_risk ?? riskLevelScore(compiledProfile.predictions?.bp_risk), fill: '#4F7CAC' },
    ].filter((x) => Number.isFinite(Number(x.score)) && Number(x.score) > 0);
    return { vitals, wearableSeries, riskScores };
  }, [compiledProfile, hub]);

  const groupedRecords = useMemo(() => {
    const records = hub?.records || [];
    const todayKey = new Date().toISOString().slice(0, 10);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = yesterday.toISOString().slice(0, 10);
    return records.reduce((groups, item) => {
      const key = item.record_date || item.created_at?.slice(0, 10) || 'Undated';
      const label = key === todayKey ? 'Today' : key === yesterdayKey ? 'Yesterday' : key;
      groups[label] = groups[label] || [];
      groups[label].push(item);
      return groups;
    }, {});
  }, [hub]);

  const abhaReadiness = useMemo(() => {
    const records = hub?.records || [];
    const ready = records.filter((item) => item.abha_ready);
    const uploaded = records.filter((item) => item.source === 'uploaded_report');
    const types = [...new Set(ready.map((item) => item.abdm_hi_type || item.record_type).filter(Boolean))];
    return {
      total: records.length,
      ready: ready.length,
      uploaded: uploaded.length,
      portable: hub?.abha_records?.length || 0,
      types,
    };
  }, [hub]);

  const profileCompletion = useMemo(() => {
    if (!compiledProfile) return { score: 0, missing: ['Create your first health profile'] };
    const checks = [
      { ok: compiledProfile.age !== '-', label: 'Age' },
      { ok: compiledProfile.height !== '-' && compiledProfile.weight !== '-', label: 'Height and weight' },
      { ok: compiledProfile.bp !== '-' || compiledProfile.sugar !== '-', label: 'BP or sugar reading' },
      { ok: compiledProfile.medical_history && compiledProfile.medical_history !== 'Not reported', label: 'Medical history' },
      { ok: compiledProfile.medications?.length > 0, label: 'Current medicines/allergies' },
      { ok: compiledProfile.family_history?.length > 0, label: 'Family history' },
      { ok: compiledProfile.sleep !== '-' || compiledProfile.steps !== '-' || compiledProfile.hr !== '-' || compiledProfile.spo2 !== '-', label: 'Wearable or lifestyle metrics' },
      { ok: compiledProfile.emergency_contacts?.length > 0, label: 'Emergency contact' },
    ];
    const completed = checks.filter((item) => item.ok).length;
    return {
      score: Math.round((completed / checks.length) * 100),
      missing: checks.filter((item) => !item.ok).map((item) => item.label),
    };
  }, [compiledProfile]);

  if (loading) {
    return <div className="max-w-5xl mx-auto px-4 py-16 text-center text-[#7A7A7A]"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Loading Health Hub...</div>;
  }

  return (
    <>
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#1A1A1A]">Health Hub</h1>
          <p className="text-[#4A4A4A] mt-2">Medication, risk, records, wearable, fitness, and wellbeing tools in one place.</p>
        </div>
        <Button onClick={() => openPdf('/health-hub/report/pdf', 'health-buddy-vault.pdf')} className="h-12 rounded-full bg-[#E07A5F] hover:bg-[#C9674D] text-white font-semibold">
          <Download className="h-4 w-4 mr-2" /> Download vault PDF
        </Button>
      </div>

      <Card className="mt-8 p-4 sm:p-5 rounded-2xl border border-[#E8E1D5] bg-white">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-2xl text-[#1A1A1A]">Health profile completeness</h2>
                <p className="text-sm text-[#7A7A7A] mt-1">Complete these once so every report is faster and more doctor-ready.</p>
              </div>
              <div className="text-2xl font-bold text-[#1E3F33]">{profileCompletion.score}%</div>
            </div>
            <Progress value={profileCompletion.score} className="mt-4 h-2" />
            {profileCompletion.missing.length > 0 && (
              <p className="mt-3 text-sm text-[#4A4A4A]">
                Missing: {profileCompletion.missing.slice(0, 4).join(', ')}
                {profileCompletion.missing.length > 4 ? ` +${profileCompletion.missing.length - 4} more` : ''}
              </p>
            )}
          </div>
          {profileCompletion.missing.includes('Emergency contact') && (
            <Button onClick={() => setActiveTab('emergency')} className="h-11 rounded-full bg-[#D9534F] hover:bg-[#C5413E] text-white font-semibold">
              <ShieldAlert className="h-4 w-4 mr-2" /> Add emergency contact
            </Button>
          )}
        </div>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8 min-w-0">
        <TabsList className="h-auto w-full flex justify-start gap-2 rounded-2xl bg-[#F4F1EA] p-2 overflow-x-auto overflow-y-hidden">
          <TabsTrigger value="profile"><HeartPulse className="h-4 w-4 mr-1" />Health Profile</TabsTrigger>
          <TabsTrigger value="abha"><IdCard className="h-4 w-4 mr-1" />ABHA</TabsTrigger>
          <TabsTrigger value="meds"><Pill className="h-4 w-4 mr-1" />Meds</TabsTrigger>
          <TabsTrigger value="risk"><HeartPulse className="h-4 w-4 mr-1" />Risk</TabsTrigger>
          <TabsTrigger value="wearable"><Watch className="h-4 w-4 mr-1" />Wearable</TabsTrigger>
          <TabsTrigger value="emergency"><ShieldAlert className="h-4 w-4 mr-1" />Emergency</TabsTrigger>
          <TabsTrigger value="records"><FileText className="h-4 w-4 mr-1" />Records</TabsTrigger>
          <TabsTrigger value="coach"><Dumbbell className="h-4 w-4 mr-1" />Coach</TabsTrigger>
          <TabsTrigger value="mental"><Brain className="h-4 w-4 mr-1" />Mental</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card className="p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-[#E8E1D5] bg-white">
            <SectionTitle icon={HeartPulse} title="My Health Profile" subtitle="Your unified, doctor-ready health summary based on recent checkups and vault updates." />
            
            {compiledProfile ? (
              <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mt-6 min-w-0">
                {(() => {
                  const suggestionsText = compiledProfile.suggestions || '';
                  if (!suggestionsText) return null;
                  const isCritical = suggestionsText.toLowerCase().includes('consult') || 
                                     suggestionsText.toLowerCase().includes('doctor') || 
                                     suggestionsText.toLowerCase().includes('critical') || 
                                     suggestionsText.toLowerCase().includes('immediate') || 
                                     compiledProfile.predictions?.bp_risk === 'high' || 
                                     compiledProfile.predictions?.sugar_risk === 'high';
                  
                  const primaryContact = compiledProfile.emergency_contacts?.[0];
                  const user = getUser();
                  const userName = user?.name || 'User';
                  const smsBody = encodeURIComponent(`CRITICAL: This is an emergency alert from HealthBuddy on behalf of ${userName}. A critical issue has been detected. Please check on me immediately.`);
                  const smsHref = primaryContact ? `sms:${primaryContact.phone}?body=${smsBody}` : `sms:?body=${smsBody}`;
                  
                  if (isCritical) {
                    return (
                      <div className="md:col-span-2 rounded-2xl bg-[#FFF5F5] p-5 border border-[#FAD2D2] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex gap-3 items-start">
                          <ShieldAlert className="h-6 w-6 text-[#D9534F] shrink-0 mt-0.5 animate-pulse" />
                          <div>
                            <h4 className="font-semibold text-base text-[#B00020] flex items-center gap-2">
                              Critical Vitals Alert
                              <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#D9534F] text-white uppercase tracking-wider">Urgent</span>
                            </h4>
                            <p className="text-xs text-[#5A5A5A] mt-1">{suggestionsText}</p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 items-end shrink-0 w-full sm:w-auto">
                          <a href={smsHref} className="w-full sm:w-auto text-center px-4 py-2 bg-[#D9534F] hover:bg-[#C5413E] text-white rounded-full text-xs font-semibold shadow-sm transition-colors">
                            Send CRITICAL SMS
                          </a>
                          {primaryContact && (
                            <span className="text-[10px] text-[#7A7A7A]">Sends to: {primaryContact.name}</span>
                          )}
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="md:col-span-2 rounded-2xl bg-[#F4F9F4] p-5 border border-[#D2E8D2] flex gap-3 items-start">
                      <HeartPulse className="h-6 w-6 text-[#2A5244] shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-base text-[#2A5244]">Health Suggestions</h4>
                        <p className="text-xs text-[#4A4A4A] mt-1">{suggestionsText}</p>
                      </div>
                    </div>
                  );
                })()}
                <div className="md:col-span-2 grid lg:grid-cols-3 gap-4 min-w-0">
                  <div className="rounded-2xl bg-[#FAFAFA] p-4 sm:p-5 border border-[#E2E8F0] min-w-0">
                    <h3 className="font-semibold text-base text-[#1E3F33] mb-3">Vitals Snapshot</h3>
                    {chartData?.vitals?.length ? (
                      <div className="h-[190px] sm:h-[210px] min-w-0">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData.vitals} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E8E1D5" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(value, name, props) => [value, `${props.payload.name} (target ${props.payload.target})`]} />
                            <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                              {chartData.vitals.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : <EmptyChart label="Complete checkup to see vitals chart" />}
                  </div>

                  <div className="rounded-2xl bg-[#FAFAFA] p-4 sm:p-5 border border-[#E2E8F0] min-w-0">
                    <h3 className="font-semibold text-base text-[#1E3F33] mb-3">Wearable & Lifestyle</h3>
                    {chartData?.wearableSeries?.length ? (
                      <div className="h-[190px] sm:h-[210px] min-w-0">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData.wearableSeries} margin={{ top: 8, right: 12, left: -24, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E8E1D5" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Line type="monotone" dataKey="value" stroke="#4F7CAC" strokeWidth={3} dot={{ r: 5, fill: '#E07A5F' }} />
                            <Line type="monotone" dataKey="target" stroke="#2A5244" strokeDasharray="4 4" strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : <EmptyChart label="Add sleep, steps, HR or SpO2" />}
                  </div>

                  <div className="rounded-2xl bg-[#FAFAFA] p-4 sm:p-5 border border-[#E2E8F0] min-w-0">
                    <h3 className="font-semibold text-base text-[#1E3F33] mb-3">Risk Overview</h3>
                    {chartData?.riskScores?.length ? (
                      <div className="h-[190px] sm:h-[210px] min-w-0">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadialBarChart innerRadius="28%" outerRadius="92%" data={chartData.riskScores} startAngle={90} endAngle={-270}>
                            <RadialBar dataKey="score" cornerRadius={8} background />
                            <Tooltip formatter={(value) => [`${value}%`, 'risk score']} />
                          </RadialBarChart>
                        </ResponsiveContainer>
                        <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-[#4A4A4A]">
                          {chartData.riskScores.map((item) => (
                            <div key={item.name} className="flex items-center gap-1 min-w-0">
                              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: item.fill }} />
                              <span className="truncate">{item.name}: {item.score}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : <EmptyChart label="Run risk/checkup to see scores" />}
                  </div>
                </div>

                {/* Section: Vitals & Body Metrics */}
                <div className="space-y-4 rounded-2xl bg-[#FAFAFA] p-5 border border-[#E2E8F0]">
                  <h3 className="font-semibold text-lg text-[#1E3F33] border-b border-[#E2E8F0] pb-2 flex items-center gap-2">
                    <Activity className="h-5 w-5 text-[#E07A5F]" /> Body Metrics &amp; Vitals
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[#7A7A7A] block">Height / Weight</span>
                      <strong className="text-[#1A1A1A]">{compiledProfile.height} cm / {compiledProfile.weight} kg</strong>
                    </div>
                    <div>
                      <span className="text-[#7A7A7A] block">BMI</span>
                      <strong className="text-[#1A1A1A]">{compiledProfile.bmi}</strong>
                    </div>
                    <div>
                      <span className="text-[#7A7A7A] block">Blood Pressure</span>
                      <strong className="text-[#1A1A1A]">{compiledProfile.bp}</strong>
                      {compiledProfile.bp === '-' && compiledProfile.predictions?.bp_risk && compiledProfile.predictions.bp_risk !== 'unknown' && (
                        <span className="text-xs block text-[#E07A5F] mt-1">
                          AI Est: {compiledProfile.predictions.bp_risk.toUpperCase()} risk
                          {compiledProfile.predictions.bp_explanation && ` (${compiledProfile.predictions.bp_explanation})`}
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="text-[#7A7A7A] block">Blood Sugar</span>
                      <strong className="text-[#1A1A1A]">{compiledProfile.sugar}</strong>
                      {compiledProfile.sugar === '-' && compiledProfile.predictions?.sugar_risk && compiledProfile.predictions.sugar_risk !== 'unknown' && (
                        <span className="text-xs block text-[#E07A5F] mt-1">
                          AI Est: {compiledProfile.predictions.sugar_risk.toUpperCase()} risk
                          {compiledProfile.predictions.sugar_explanation && ` (${compiledProfile.predictions.sugar_explanation})`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section: Medications */}
                <div className="space-y-4 rounded-2xl bg-[#FAFAFA] p-5 border border-[#E2E8F0]">
                  <h3 className="font-semibold text-lg text-[#1E3F33] border-b border-[#E2E8F0] pb-2 flex items-center gap-2">
                    <Pill className="h-5 w-5 text-[#E07A5F]" /> Active Medications
                  </h3>
                  {compiledProfile.medications.length ? (
                    <ul className="space-y-2 text-sm">
                      {compiledProfile.medications.map((m, i) => (
                        <li key={i} className="flex justify-between border-b border-dotted pb-1.5 last:border-0 last:pb-0">
                          <strong className="text-[#1A1A1A]">{m.name}</strong>
                          <span className="text-[#4A4A4A]">{m.dosage} • {m.schedule}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-[#7A7A7A]">No active medications reported.</p>
                  )}
                </div>

                {/* Section: Medical History */}
                <div className="space-y-4 rounded-2xl bg-[#FAFAFA] p-5 border border-[#E2E8F0] md:col-span-2">
                  <h3 className="font-semibold text-lg text-[#1E3F33] border-b border-[#E2E8F0] pb-2 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#E07A5F]" /> Medical History &amp; Family History
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-[#7A7A7A] block font-semibold">Conditions &amp; Allergies</span>
                      {medicalHistory.plain ? (
                        <p className="text-[#1A1A1A] mt-1 leading-relaxed">{medicalHistory.plain}</p>
                      ) : (
                        <div className="mt-3 space-y-4">
                          {medicalHistory.symptoms.length ? (
                            <div className="rounded-xl border border-[#E8E1D5] bg-white p-3">
                              <div className="mb-2 flex items-center justify-between gap-3">
                                <span className="font-semibold text-[#1E3F33]">Main symptoms mentioned</span>
                                <span className="text-xs text-[#7A7A7A]">{medicalHistory.symptoms.length} item{medicalHistory.symptoms.length === 1 ? '' : 's'}</span>
                              </div>
                              <HistoryPills items={medicalHistory.symptoms} tone="neutral" />
                            </div>
                          ) : null}

                          {medicalHistory.positives.length ? (
                            <div className="rounded-xl border border-[#B8D8C2] bg-[#FBFEFC] p-3">
                              <div className="mb-2 flex items-center justify-between gap-3">
                                <span className="font-semibold text-[#1E3F33]">Reported in full checkup</span>
                                <span className="text-xs text-[#7A7A7A]">{medicalHistory.positives.length} yes</span>
                              </div>
                              <HistoryPills items={medicalHistory.positives} tone="positive" />
                            </div>
                          ) : null}

                          {medicalHistory.negatives.length ? (
                            <div className="rounded-xl border border-[#D8DDE5] bg-[#F8FAFC] p-3">
                              <div className="mb-2 flex items-center justify-between gap-3">
                                <span className="font-semibold text-[#4A4A4A]">Checked but not reported</span>
                                <span className="text-xs text-[#7A7A7A]">{medicalHistory.negatives.length} no</span>
                              </div>
                              <HistoryPills items={medicalHistory.negatives} tone="muted" limit={10} />
                            </div>
                          ) : null}

                          {medicalHistory.other.length ? (
                            <div className="rounded-xl border border-[#E8E1D5] bg-white p-3">
                              <span className="font-semibold text-[#1E3F33]">Other notes</span>
                              <ul className="mt-2 list-disc pl-5 text-[#1A1A1A]">
                                {medicalHistory.other.map((item, index) => <li key={index}>{item}</li>)}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                    {compiledProfile.family_history?.length ? (
                      <div>
                        <span className="text-[#7A7A7A] block font-semibold">Family History</span>
                        <p className="text-[#1A1A1A] mt-1">{compiledProfile.family_history.join(', ')}</p>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Section: Lifestyle & Wearable Trends */}
                <div className="space-y-4 rounded-2xl bg-[#FAFAFA] p-5 border border-[#E2E8F0]">
                  <h3 className="font-semibold text-lg text-[#1E3F33] border-b border-[#E2E8F0] pb-2 flex items-center gap-2">
                    <Watch className="h-5 w-5 text-[#E07A5F]" /> Lifestyle &amp; Wearables
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[#7A7A7A] block">Daily Sleep</span>
                      <strong className="text-[#1A1A1A]">{compiledProfile.sleep} hrs</strong>
                    </div>
                    <div>
                      <span className="text-[#7A7A7A] block">Steps Goal</span>
                      <strong className="text-[#1A1A1A]">{compiledProfile.steps}</strong>
                    </div>
                    <div>
                      <span className="text-[#7A7A7A] block">Heart Rate</span>
                      <strong className="text-[#1A1A1A]">{compiledProfile.hr} bpm</strong>
                    </div>
                    <div>
                      <span className="text-[#7A7A7A] block">SpO2 (Oxygen)</span>
                      <strong className="text-[#1A1A1A]">{compiledProfile.spo2}%</strong>
                    </div>
                  </div>
                </div>

                {/* Section: Emergency Details */}
                <div className="space-y-4 rounded-2xl bg-[#FAFAFA] p-5 border border-[#E2E8F0]">
                  <h3 className="font-semibold text-lg text-[#1E3F33] border-b border-[#E2E8F0] pb-2 flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5 text-[#E07A5F]" /> Emergency Contacts
                  </h3>
                  {compiledProfile.emergency_contacts.length ? (
                    <ul className="space-y-2 text-sm">
                      {compiledProfile.emergency_contacts.map((c, i) => (
                        <li key={i} className="flex justify-between border-b border-dotted pb-1.5 last:border-0 last:pb-0">
                          <strong className="text-[#1A1A1A]">{c.name}</strong>
                          <span className="text-[#4A4A4A]">{c.phone}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-[#7A7A7A]">No emergency contact set.</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-center text-[#7A7A7A] py-10">No profile details available yet. Start a checkup consultation with Buddy to generate one automatically.</p>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="abha">
          <Card className="p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-[#E8E1D5] bg-white">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <SectionTitle icon={IdCard} title="ABHA-ready Profile" subtitle="Your HealthBuddy profile prepared for ABDM identity matching and future consent-based record exchange." />
              {hub?.abha?.linked ? (
                <Button onClick={unlinkAbha} disabled={busy === 'abha-unlink'} variant="outline" className="rounded-full border-[#1E3F33] text-[#1E3F33] shrink-0">
                  {busy === 'abha-unlink' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlink className="h-4 w-4 mr-2" />}
                  Unlink ABHA
                </Button>
              ) : (
                <Button onClick={() => setAbhaOpen(true)} className="rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white shrink-0">
                  <ShieldCheck className="h-4 w-4 mr-2" /> Link ABHA
                </Button>
              )}
            </div>

            <div className="mt-6 grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 rounded-2xl border border-[#E2E8F0] bg-[#FAFAFA] p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-[#7A7A7A]">ABHA link status</div>
                    <h3 className="mt-1 text-2xl font-bold text-[#1E3F33]">
                      {hub?.abha?.linked ? 'Linked with ABDM' : 'Not linked yet'}
                    </h3>
                  </div>
                  <span className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${hub?.abha?.linked ? 'bg-[#E8F6EE] text-[#1E6B45] border border-[#B8D8C2]' : 'bg-[#FFF7ED] text-[#9A5B00] border border-[#F4D19B]'}`}>
                    {hub?.abha?.linked ? 'ABHA verified' : 'Ready to link'}
                  </span>
                </div>

                <div className="mt-5 grid sm:grid-cols-2 gap-3">
                  <ProfileField label="ABHA number/address" value={hub?.abha?.masked || 'Not linked'} />
                  <ProfileField label="ABHA address" value={hub?.abha?.abha_address_masked || 'Available after linking'} />
                  <ProfileField label="Linked on" value={hub?.abha?.linked_at ? hub.abha.linked_at.slice(0, 10) : '-'} />
                  <ProfileField label="Authentication mode" value={hub?.abha?.auth_mode || '-'} />
                  <ProfileField label="Consent manager" value={hub?.abha?.cm_id || 'sbx'} />
                  <ProfileField label="HIP ID" value={hub?.abha?.hip_id || 'HealthBuddy local'} />
                </div>

                <p className="mt-4 text-xs leading-5 text-[#7A7A7A]">
                  ABHA is used as a digital health identity. It does not automatically share your reports. Health records should move through ABDM only after user consent and approved production HIP/HIU flow.
                </p>
              </div>

              <div className="rounded-2xl border border-[#E2E8F0] bg-[#FAFAFA] p-4 sm:p-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-[#7A7A7A]">ABHA-ready records</div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-white border border-[#E2E8F0] p-3">
                    <div className="text-2xl font-bold text-[#1E3F33]">{abhaReadiness.ready}</div>
                    <div className="text-xs text-[#7A7A7A]">Ready records</div>
                  </div>
                  <div className="rounded-xl bg-white border border-[#E2E8F0] p-3">
                    <div className="text-2xl font-bold text-[#1E3F33]">{abhaReadiness.portable}</div>
                    <div className="text-xs text-[#7A7A7A]">FHIR metadata</div>
                  </div>
                  <div className="rounded-xl bg-white border border-[#E2E8F0] p-3">
                    <div className="text-2xl font-bold text-[#1E3F33]">{abhaReadiness.uploaded}</div>
                    <div className="text-xs text-[#7A7A7A]">Uploads</div>
                  </div>
                  <div className="rounded-xl bg-white border border-[#E2E8F0] p-3">
                    <div className="text-2xl font-bold text-[#1E3F33]">{abhaReadiness.total}</div>
                    <div className="text-xs text-[#7A7A7A]">Vault total</div>
                  </div>
                </div>
                {abhaReadiness.types.length ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {abhaReadiness.types.map((type) => (
                      <span key={type} className="rounded-full border border-[#B8D8C2] bg-white px-3 py-1 text-xs font-semibold text-[#1E3F33]">
                        {String(type).replaceAll('_', ' ')}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-[#7A7A7A]">Generate a report or upload records to create ABHA-ready metadata.</p>
                )}
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-[#E2E8F0] bg-[#FAFAFA] p-4 sm:p-5">
              <h3 className="font-semibold text-[#1E3F33]">ABHA holder profile contents</h3>
              <p className="mt-1 text-sm text-[#7A7A7A]">
                These are the identity/profile fields HealthBuddy can show after ABHA linking, depending on what ABDM returns for the verified holder.
              </p>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <ProfileField label="Name" value={hub?.abha?.profile?.name || getUser()?.name} />
                <ProfileField label="Gender" value={hub?.abha?.profile?.gender} />
                <ProfileField label="Date of birth" value={hub?.abha?.profile?.date_of_birth || hub?.abha?.profile?.year_of_birth} />
                <ProfileField label="Mobile" value={hub?.abha?.profile?.mobile || getUser()?.phone} />
                <ProfileField label="Email" value={hub?.abha?.profile?.email || getUser()?.email} />
                <ProfileField label="KYC status" value={hub?.abha?.profile?.kyc_status} />
                <ProfileField label="HealthBuddy profile" value={`${profileCompletion.score}% complete`} />
                <ProfileField label="Record portability" value={`${abhaReadiness.ready}/${abhaReadiness.total} records ready`} />
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="meds">
          <Card className="p-6 rounded-3xl border border-[#E8E1D5] bg-white">
            <SectionTitle icon={Pill} title="Smart Medication Management" subtitle="Track dosage, missed doses, refills, and medicine safety warnings." />
            <div className="mt-6 space-y-4">
              {medications.map((med, index) => (
                <div key={index} className="grid md:grid-cols-5 gap-3 rounded-2xl bg-[#FAFAFA] p-4 border border-[#E2E8F0]">
                  <Input placeholder="Medicine" value={med.name} onChange={(e) => setMedications((list) => list.map((m, i) => i === index ? { ...m, name: e.target.value } : m))} />
                  <Input placeholder="Dosage" value={med.dosage} onChange={(e) => setMedications((list) => list.map((m, i) => i === index ? { ...m, dosage: e.target.value } : m))} />
                  <Input placeholder="Schedule" value={med.schedule} onChange={(e) => setMedications((list) => list.map((m, i) => i === index ? { ...m, schedule: e.target.value } : m))} />
                  <Input type="date" value={med.refill_date || ''} onChange={(e) => setMedications((list) => list.map((m, i) => i === index ? { ...m, refill_date: e.target.value } : m))} />
                  <Input type="number" min="0" placeholder="Missed" value={med.missed_count || ''} onChange={(e) => setMedications((list) => list.map((m, i) => i === index ? { ...m, missed_count: Number(e.target.value) } : m))} />
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => setMedications((m) => [...m, emptyMedication])} className="rounded-full border-[#1E3F33] text-[#1E3F33]"><Plus className="h-4 w-4 mr-2" />Add medicine</Button>
              <Button onClick={() => run('meds', () => api.post('/health-hub/medications', { medications: medications.filter((m) => m.name && m.dosage && m.schedule) }))} className="rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white">
                {busy === 'meds' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Save
              </Button>
            </div>
            <ResultList items={hub?.medication_warnings} />
          </Card>
        </TabsContent>

        <TabsContent value="risk">
          <Card className="p-6 rounded-3xl border border-[#E8E1D5] bg-white">
            <SectionTitle icon={HeartPulse} title="Health Risk Prediction" subtitle="Estimate risk using age, BMI, BP, sugar, and family history." />
            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              {['age', 'weight_kg', 'height_cm', 'systolic_bp', 'diastolic_bp', 'blood_sugar_mg_dl'].map((key) => (
                <div key={key}>
                  <Label className="text-[#1E3F33] font-semibold">{key.replaceAll('_', ' ')}</Label>
                  <Input type="number" value={risk[key]} onChange={(e) => setRisk({ ...risk, [key]: e.target.value })} className="mt-2" />
                </div>
              ))}
              <div className="sm:col-span-3">
                <Label className="text-[#1E3F33] font-semibold">Family history</Label>
                <Input value={risk.family_history} onChange={(e) => setRisk({ ...risk, family_history: e.target.value })} placeholder="diabetes, BP, heart disease" className="mt-2" />
              </div>
            </div>
            <Button disabled={!risk.age || !risk.weight_kg || !risk.height_cm} onClick={() => run('risk', () => api.post('/health-hub/risk', riskPayload))} className="mt-5 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white">
              {busy === 'risk' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Activity className="h-4 w-4 mr-2" />}Calculate
            </Button>
            {hub?.risk && (
              <div className="mt-6 space-y-4">
                <div className="font-semibold text-[#1A1A1A]">BMI: {hub.risk.bmi}</div>
                {hub.risk.risks?.map((item) => (
                  <div key={item.condition}>
                    <div className="flex justify-between text-sm font-semibold"><span>{item.condition}</span><span>{item.score}/100 - {item.level}</span></div>
                    <Progress value={item.score} className="mt-2 h-3" />
                  </div>
                ))}
                <ResultList items={hub.risk.preventive_actions} />
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="wearable">
          <Card className="p-6 rounded-3xl border border-[#E8E1D5] bg-white">
            <SectionTitle icon={Watch} title="Wearable Integration" subtitle="Log smartwatch or fitness tracker readings and get personalized recommendations." />
            <div className="grid sm:grid-cols-4 gap-4 mt-6">
              {['heart_rate', 'sleep_hours', 'steps', 'spo2'].map((key) => (
                <div key={key}>
                  <Label className="text-[#1E3F33] font-semibold">{key.replaceAll('_', ' ')}</Label>
                  <Input type="number" value={wearable[key]} onChange={(e) => setWearable({ ...wearable, [key]: e.target.value })} className="mt-2" />
                </div>
              ))}
            </div>
            <Button onClick={() => run('wearable', () => api.post('/health-hub/wearable', Object.fromEntries(Object.entries(wearable).map(([k, v]) => [k, v === '' ? null : Number(v)]))))} className="mt-5 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white">
              {busy === 'wearable' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Save metrics
            </Button>
            <ResultList items={hub?.wearable?.recommendations} />

            {/* External App Integration Guide */}
            <div className="mt-8 border-t border-[#E8E1D5] pt-6 space-y-4">
              <div>
                <h3 className="font-semibold text-lg text-[#1E3F33] flex items-center gap-2">
                  <Watch className="h-5 w-5 text-[#E07A5F]" /> Monitor Vitals Externally
                </h3>
                <p className="text-sm text-[#4A4A4A] mt-1">
                  Connect your smartwatch, Tasker, Home Assistant, or family monitoring widgets directly using your secure sync credentials.
                </p>
              </div>

              <div className="rounded-2xl bg-[#FAFAFA] p-5 border border-[#E2E8F0] space-y-4">
                <div>
                  <span className="text-xs font-semibold text-[#7A7A7A] uppercase tracking-wider block">Your Secure Wearable Sync Token</span>
                  <div className="flex gap-2 mt-1.5 items-center">
                    <code className="text-xs bg-[#F4F1EA] px-3 py-2 rounded-lg font-mono text-[#1E3F33] break-all select-all flex-1 border border-[#E2E8F0]">
                      {wearableToken || 'Generating token...'}
                    </code>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        if (wearableToken) {
                          navigator.clipboard.writeText(wearableToken);
                          toast.success('Copied token to clipboard!');
                        }
                      }}
                      className="rounded-full shrink-0"
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 pt-2 border-t border-[#E2E8F0]">
                  <div className="space-y-2">
                    <span className="text-xs font-semibold text-[#1E3F33] uppercase tracking-wider block">1. Live JSON Feed URL</span>
                    <p className="text-xs text-[#5A5A5A] leading-relaxed">
                      Fetch your latest wearable vitals securely in raw JSON for widgets or personal dashboards.
                    </p>
                    <div className="flex gap-2">
                      <code className="text-[10px] bg-[#F4F1EA] p-2 rounded-lg font-mono text-[#1E3F33] break-all flex-1 border border-[#E2E8F0]">
                        {wearableToken ? `${API}/public/health-hub/wearables/feed?token=${wearableToken}` : 'Loading...'}
                      </code>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          if (wearableToken) {
                            navigator.clipboard.writeText(`${API}/public/health-hub/wearables/feed?token=${wearableToken}`);
                            toast.success('Copied Live Feed URL!');
                          }
                        }}
                        className="h-8 rounded-full shrink-0 text-xs px-3"
                      >
                        Copy
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="text-xs font-semibold text-[#1E3F33] uppercase tracking-wider block">2. External Webhook (HTTP POST)</span>
                    <p className="text-xs text-[#5A5A5A] leading-relaxed">
                      Push step counts, heart rate, or sleep hours from external scripts or task schedulers.
                    </p>
                    <div className="flex gap-2">
                      <code className="text-[10px] bg-[#F4F1EA] p-2 rounded-lg font-mono text-[#1E3F33] break-all flex-1 border border-[#E2E8F0]">
                        {wearableToken ? `curl -X POST -H "Content-Type: application/json" -d '{"heart_rate": 72, "sleep_hours": 7.5, "steps": 8420, "spo2": 98}' "${API}/health-hub/wearables/sync?token=${wearableToken}"` : 'Loading...'}
                      </code>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          if (wearableToken) {
                            navigator.clipboard.writeText(`curl -X POST -H "Content-Type: application/json" -d '{"heart_rate": 72, "sleep_hours": 7.5, "steps": 8420, "spo2": 98}' "${API}/health-hub/wearables/sync?token=${wearableToken}"`);
                            toast.success('Copied curl command!');
                          }
                        }}
                        className="h-8 rounded-full shrink-0 text-xs px-3"
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#E2E8F0] space-y-3">
                  <span className="text-xs font-semibold text-[#1E3F33] uppercase tracking-wider block">3. Simple Mobile App Integration (Free for Android & iOS)</span>
                  <p className="text-xs text-[#5A5A5A] leading-relaxed">
                    If you do not want to use terminal commands, you can link your smartwatch or fitness band using free mobile apps:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4 text-xs mt-2">
                    <div className="p-4 rounded-xl border border-[#E2E8F0] bg-white space-y-2">
                      <h4 className="font-semibold text-[#1E3F33] flex items-center gap-1.5">
                        <span className="inline-block px-1.5 py-0.5 rounded bg-[#E8E1D5] text-[#1E3F33] font-bold text-[10px]">Android Users</span>
                        HTTP Request Shortcuts or MacroDroid
                      </h4>
                      <p className="text-[#5A5A5A] leading-relaxed">
                        1. Download <strong>HTTP Request Shortcuts</strong> for free from the Google Play Store.<br />
                        2. Create a new Shortcut with Method <strong>POST</strong> and paste your Webhook URL above.<br />
                        3. Set Request Body to <strong>Custom/JSON</strong> and specify: <code className="bg-[#F4F1EA] px-1 py-0.5 rounded text-[#1E3F33]">{"{ \"steps\": 8000 }"}</code>. You can trigger this shortcut with one click from a Home Screen widget!
                      </p>
                    </div>
                    <div className="p-4 rounded-xl border border-[#E2E8F0] bg-white space-y-2">
                      <h4 className="font-semibold text-[#1E3F33] flex items-center gap-1.5">
                        <span className="inline-block px-1.5 py-0.5 rounded bg-[#1E3F33] text-white font-bold text-[10px]">iPhone Users</span>
                        Apple Shortcuts (Built-in & Free)
                      </h4>
                      <p className="text-[#5A5A5A] leading-relaxed">
                        1. Open the <strong>Shortcuts</strong> app on your iPhone.<br />
                        2. Create a new Shortcut and add the action <strong>"Find Health Samples"</strong> to fetch your steps or heart rate.<br />
                        3. Add the action <strong>"Get Contents of URL"</strong>. Set URL to your sync webhook, Method to <strong>POST</strong>, and pass your health metrics as JSON. You can set it to run automatically every night!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="emergency">
          <Card className="p-6 rounded-3xl border border-[#E8E1D5] bg-white">
            <SectionTitle icon={ShieldAlert} title="Emergency Detection System" subtitle="Detect urgent messages and prepare emergency-contact notification guidance." />
            
            {showEmergencyOnboarding && (!hub?.emergency_contacts || hub.emergency_contacts.length === 0) && (
              <div className="mt-6 p-5 rounded-2xl bg-[#FFF5F5] border border-[#FAD2D2] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex gap-3 items-start">
                  <ShieldAlert className="h-6 w-6 text-[#D9534F] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-base text-[#B00020]">
                      First Time Setting Up Emergency Alerts?
                    </h4>
                    <p className="text-xs text-[#5A5A5A] mt-1">
                      It looks like you haven't set up an emergency contact yet. Please add a trusted contact below so you can send SMS alerts if critical issues are detected. We will only ask you this once.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0 w-full sm:w-auto justify-end">
                  <Button 
                    size="sm" 
                    onClick={() => {
                      document.getElementById('contact-name-input')?.focus();
                    }}
                    className="rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white text-xs font-semibold"
                  >
                    Setup Now
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => {
                      localStorage.setItem('hb_emergency_onboarding_seen', 'true');
                      setShowEmergencyOnboarding(false);
                    }}
                    className="rounded-full text-[#7A7A7A] hover:bg-[#F4F1EA] text-xs font-semibold border border-[#E8E1D5]"
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            )}

            <Textarea value={emergencyText} onChange={(e) => setEmergencyText(e.target.value)} placeholder="Example: chest pain and can't breathe" className="mt-6 min-h-28" />
            <Button disabled={!emergencyText.trim()} onClick={async () => {
              setBusy('emergency');
              try {
                const { data } = await api.post('/health-hub/emergency/check', { session_id: 'health-hub', message: emergencyText });
                setEmergencyResult(data);
                toast[data.emergency ? 'error' : 'success'](data.emergency ? 'Emergency signs detected' : 'No emergency keyword detected');
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Could not check urgency');
              } finally {
                setBusy('');
              }
            }} className="mt-4 rounded-full bg-[#D9534F] hover:bg-[#C5413E] text-white">
              <AlertTriangle className="h-4 w-4 mr-2" />Check urgency
            </Button>
            <ResultList items={emergencyResult?.guidance} />
            <div className="grid sm:grid-cols-3 gap-3 mt-6">
              <Input id="contact-name-input" placeholder="Contact name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
              <Input placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
              <Button onClick={() => run('contact', async () => {
                await api.post('/health-hub/emergency-contacts', { ...contact, enabled: true });
                localStorage.setItem('hb_emergency_onboarding_seen', 'true');
                setShowEmergencyOnboarding(false);
                setContact({ name: '', phone: '' });
              })} className="rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white">Save contact</Button>
            </div>
            <ResultList items={hub?.emergency_contacts?.map((c) => `${c.name}: ${c.phone}`)} />
          </Card>
        </TabsContent>

        <TabsContent value="records">
          <Card className="p-6 rounded-3xl border border-[#E8E1D5] bg-white">
            <SectionTitle icon={FileText} title="Health Records Vault" subtitle="Reports and uploaded records are grouped by consultation date." />
            <div className="grid sm:grid-cols-4 gap-4 mt-6">
              <Select value={record.record_type} onValueChange={(v) => setRecord({ ...record, record_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prescription">Prescription</SelectItem>
                  <SelectItem value="lab_report">Lab report</SelectItem>
                  <SelectItem value="medical_history">Medical history</SelectItem>
                  <SelectItem value="vaccination">Vaccination</SelectItem>
                  <SelectItem value="discharge_summary">Discharge summary</SelectItem>
                  <SelectItem value="uploaded_report">Uploaded report</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Title" value={record.title} onChange={(e) => setRecord({ ...record, title: e.target.value })} />
              <Input type="date" value={record.record_date} onChange={(e) => setRecord({ ...record, record_date: e.target.value })} />
              <Button onClick={() => run('record', () => api.post('/health-hub/records', record))} className="rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white">Add record</Button>
            </div>
            <Textarea placeholder="Record details" value={record.details} onChange={(e) => setRecord({ ...record, details: e.target.value })} className="mt-4 min-h-28" />

            <div className="mt-6 rounded-2xl border border-dashed border-[#B8D8C2] bg-[#F6FBF8] p-4">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-[#1E3F33] flex items-center gap-2">
                    <Upload className="h-4 w-4" /> Upload external report
                  </h3>
                  <p className="mt-1 text-sm text-[#5A5A5A]">
                    Add PDFs or images from outside checkups. The file and notes will appear in Health Profile, Health Records, and ABHA-ready data.
                  </p>
                </div>
                <Button
                  type="button"
                  disabled={!uploadFile || busy === 'upload-record'}
                  onClick={uploadHealthRecord}
                  className="rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white shrink-0"
                >
                  {busy === 'upload-record' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  Save upload
                </Button>
              </div>
              <div className="grid sm:grid-cols-4 gap-4 mt-4">
                <Input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                />
                <Select value={uploadRecord.record_type} onValueChange={(v) => setUploadRecord({ ...uploadRecord, record_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uploaded_report">General report</SelectItem>
                    <SelectItem value="lab_report">Lab report</SelectItem>
                    <SelectItem value="prescription">Prescription</SelectItem>
                    <SelectItem value="medical_history">Medical history</SelectItem>
                    <SelectItem value="discharge_summary">Discharge summary</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Report title" value={uploadRecord.title} onChange={(e) => setUploadRecord({ ...uploadRecord, title: e.target.value })} />
                <Input type="date" value={uploadRecord.record_date} onChange={(e) => setUploadRecord({ ...uploadRecord, record_date: e.target.value })} />
              </div>
              <Textarea
                placeholder="Optional notes: hospital name, abnormal values, medicines advised, doctor comments"
                value={uploadRecord.notes}
                onChange={(e) => setUploadRecord({ ...uploadRecord, notes: e.target.value })}
                className="mt-4 min-h-24 bg-white"
              />
            </div>

            <div className="mt-8 space-y-6">
              {Object.keys(groupedRecords).length ? Object.entries(groupedRecords).map(([dateLabel, items]) => (
                <div key={dateLabel} className="rounded-2xl border border-[#E2E8F0] bg-[#FAFAFA] p-4">
                  <div className="text-sm font-semibold text-[#1E3F33]">{dateLabel}</div>
                  <div className="mt-3 grid gap-3">
                    {items.map((r) => (
                      <div key={r.id} className="rounded-xl border border-[#E2E8F0] bg-white p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-[#7A7A7A]">{r.source === 'health_buddy_report' ? 'Health Buddy Report' : r.record_type?.replaceAll('_', ' ')}</div>
                            <h3 className="mt-1 font-semibold text-[#1A1A1A]">{r.title}</h3>
                            {r.abha_ready ? (
                              <div className="mt-2 inline-flex rounded-full border border-[#C8E6D6] bg-[#F0FAF4] px-3 py-1 text-xs font-semibold text-[#1E6B45]">
                                ABHA-ready: {r.abdm_hi_type || 'Health record'}
                              </div>
                            ) : null}
                            <p className="mt-1 text-sm text-[#4A4A4A]">{r.details?.slice(0, 180)}{r.details?.length > 180 ? '...' : ''}</p>
                          </div>
                          {(r.report_id || r.file_url) ? (
                            <div className="flex flex-wrap gap-2 shrink-0">
                              {r.file_url ? (
                                <Button type="button" variant="outline" onClick={() => openFile(r.file_url, r.file_name || r.title || 'health-report')} className="rounded-full border-[#1E3F33] text-[#1E3F33]">
                                  <Download className="h-4 w-4 mr-1" /> File
                                </Button>
                              ) : null}
                              {r.report_id ? (
                                <>
                              <Button type="button" variant="outline" onClick={() => nav(`/report/${r.report_id}`)} className="rounded-full border-[#1E3F33] text-[#1E3F33]">Open</Button>
                              <Button type="button" variant="ghost" onClick={() => openPdf(`/report/${r.report_id}/pdf`, `health-buddy-${r.report_id}.pdf`)} className="rounded-full text-[#1E3F33]">
                                <Download className="h-4 w-4 mr-1" /> PDF
                              </Button>
                                </>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )) : (
                <p className="mt-6 text-sm text-[#7A7A7A]">No records yet. Generate a report or add a record above.</p>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="coach">
          <Card className="p-6 rounded-3xl border border-[#E8E1D5] bg-white">
            <SectionTitle icon={Dumbbell} title="Diet and Fitness Coach" subtitle="Generate meal and exercise plans based on health conditions and goals." />
            <div className="grid sm:grid-cols-4 gap-4 mt-6">
              <Input placeholder="Condition" value={diet.condition} onChange={(e) => setDiet({ ...diet, condition: e.target.value })} />
              <Input placeholder="Goal" value={diet.goal} onChange={(e) => setDiet({ ...diet, goal: e.target.value })} />
              <Input type="number" placeholder="Calories" value={diet.calories} onChange={(e) => setDiet({ ...diet, calories: e.target.value })} />
              <Input placeholder="Activity level" value={diet.activity_level} onChange={(e) => setDiet({ ...diet, activity_level: e.target.value })} />
            </div>
            <Button onClick={() => run('diet', () => api.post('/health-hub/diet-fitness', { ...diet, calories: diet.calories ? Number(diet.calories) : null }))} className="mt-5 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white">
              <Dumbbell className="h-4 w-4 mr-2" />Generate plan
            </Button>
            <ResultList items={[...(hub?.diet_fitness?.meal_plan || []), ...(hub?.diet_fitness?.exercise_plan || [])]} />
          </Card>
        </TabsContent>

        <TabsContent value="mental">
          <Card className="p-6 rounded-3xl border border-[#E8E1D5] bg-white">
            <SectionTitle icon={Brain} title="Mental Health Support" subtitle="Track mood, stress, sleep quality, wellness exercises, and escalation needs." />
            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              {['mood', 'stress', 'sleep_quality'].map((key) => (
                <div key={key}>
                  <Label className="text-[#1E3F33] font-semibold">{key.replaceAll('_', ' ')}: {mental[key]}</Label>
                  <Input type="range" min="1" max="5" value={mental[key]} onChange={(e) => setMental({ ...mental, [key]: Number(e.target.value) })} className="mt-2" />
                </div>
              ))}
            </div>
            <Textarea value={mental.notes} onChange={(e) => setMental({ ...mental, notes: e.target.value })} placeholder="Notes" className="mt-4 min-h-24" />
            <Button onClick={() => run('mental', () => api.post('/health-hub/mental-health', mental))} className="mt-4 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white">
              <Brain className="h-4 w-4 mr-2" />Save check-in
            </Button>
            <ResultList items={[hub?.mental_health?.escalation, ...(hub?.mental_health?.wellness_exercises || [])].filter(Boolean)} />
          </Card>
        </TabsContent>
      </Tabs>
    </div>

    <Dialog open={abhaOpen} onOpenChange={setAbhaOpen}>
      <DialogContent className="rounded-3xl border border-[#E8E1D5] bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A1A1A]">Link ABHA</DialogTitle>
          <DialogDescription>
            Verify your ABHA with ABDM and attach it to HealthBuddy for identity matching and future consent-based record exchange.
          </DialogDescription>
        </DialogHeader>

        {abhaStep === 'id' ? (
          <form onSubmit={requestAbhaOtp} className="space-y-4">
            <div>
              <Label className="font-semibold text-[#1E3F33]">ABHA number or address</Label>
              <div className="relative mt-2">
                <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
                <Input
                  value={abhaId}
                  onChange={(e) => setAbhaId(e.target.value)}
                  placeholder="12-3456-7890-12 or name@abdm"
                  className="h-12 pl-10 rounded-xl border-[#E2E8F0]"
                  autoFocus
                />
              </div>
              <p className="mt-2 text-xs leading-5 text-[#7A7A7A]">
                HealthBuddy asks ABDM for supported authentication modes. The OTP is sent by ABDM to the mobile linked with that ABHA.
              </p>
            </div>
            <Button disabled={busy === 'abha-request' || abhaId.trim().length < 3} className="w-full h-12 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold">
              {busy === 'abha-request' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Send OTP
            </Button>
          </form>
        ) : (
          <form onSubmit={verifyAbhaOtp} className="space-y-4">
            <div>
              <Label className="font-semibold text-[#1E3F33]">OTP</Label>
              <div className="relative mt-2">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
                <Input
                  value={abhaOtp}
                  onChange={(e) => setAbhaOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6 digit OTP"
                  className="h-12 pl-10 rounded-xl border-[#E2E8F0]"
                  inputMode="numeric"
                  autoFocus
                />
              </div>
              <p className="mt-2 text-xs leading-5 text-[#7A7A7A]">
                Enter the OTP sent by ABDM. Linking ABHA does not share records automatically.
              </p>
            </div>
            <div className="flex gap-2">
              <Button type="button" onClick={() => setAbhaStep('id')} variant="outline" className="h-12 rounded-full border-[#1E3F33] text-[#1E3F33]">
                Back
              </Button>
              <Button disabled={busy === 'abha-verify' || abhaOtp.length !== 6} className="flex-1 h-12 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold">
                {busy === 'abha-verify' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                Verify and link
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
